/**
 * The customer-facing source of truth for how uploaded values become numbers
 * and dates.
 *
 * This module is PURE DATA (a type-only import, no runtime deps) so it can be
 * rendered on the public /parsing-rules page and imported anywhere.
 * {@link PARSING_EXAMPLES} is the numeric contract: `numeric-parse.test.ts`
 * asserts `parseNumericValue(input) === parsed` for every row, so the parser can
 * never drift from what this page promises. {@link DATE_PARSING_EXAMPLES} is the
 * date contract, pinned the same way by `date-parse.test.ts` against
 * `parseDateWithFormat(input, format)`. When you change either grammar
 * ({@link ./numeric-parse} / {@link ./date-parse}), update these examples (the
 * tests go red otherwise), and vice versa.
 *
 * STYLE: no em-dashes anywhere in this file (shipped UI copy convention).
 */

import type { DeclaredDateFormat } from './declared-column-facts';

/** One human-readable parsing rule, rendered as a titled paragraph on the page. */
export interface ParsingRule {
  readonly id: string;
  readonly title: string;
  readonly detail: string;
}

/**
 * One worked example. `parsed` is BOTH the test oracle (it must equal
 * `parseNumericValue(input)`) and what the page shows as the result, so the page is
 * guaranteed to match the parser. `null` means the value is not recognized as a
 * number and is kept as text.
 */
export interface ParsingExample {
  readonly input: string;
  readonly parsed: number | null;
  readonly note: string;
}

export const PARSING_RULES: readonly ParsingRule[] = [
  {
    id: 'one-grammar',
    title: 'One consistent rule for every column',
    detail:
      'A value is read the same way no matter which field it maps to. The rules below decide whether a cell is a number and, if so, what number it becomes. A field stays numeric as long as most of its values are numbers; the occasional value that is not a number (such as "Pending") is kept but treated as empty.',
  },
  {
    id: 'blank-not-zero',
    title: 'Blank is empty, never zero',
    detail:
      'An empty cell and common placeholders for missing data (NA, N/A, none, a single dash) are treated as "no value". They are never read as 0, so they do not pull an average or a total down.',
  },
  {
    id: 'plain-numbers',
    title: 'Plain numbers, signs, and decimals',
    detail:
      'Whole numbers and decimals are read as written, with an optional leading + or - sign. A leading or trailing space is ignored.',
  },
  {
    id: 'thousands',
    title: 'Thousands separators',
    detail:
      'Commas are removed only when they group digits correctly in threes, so 1,234.56 reads as 1234.56 and 1,234,567 reads as 1234567. The decimal point is always a period. A value whose commas do not group correctly (1,5 or 12,34 or 1,2345 or a zero-led 0,125) is never silently reinterpreted as a different number: it is kept as text. Numbers written in the European style, with a period for thousands and a comma for the decimal (1.234,56), are also kept as text and should be reformatted before upload.',
  },
  {
    id: 'currency',
    title: 'Currency symbols and codes',
    detail:
      'A leading currency symbol ($, and the euro, pound, or yen symbols) and a leading or trailing 3-letter currency code (USD, EUR, GBP, JPY, CAD, AUD, CHF, CNY) are removed, so $1,234.56 and 1234.56 USD both read as 1234.56. The sign and the symbol can appear in either order: -$1,234.56 and $-1,234.56 both read as -1234.56. A currency marker by itself with no number stays text.',
  },
  {
    id: 'parentheses',
    title: 'Parentheses mean negative',
    detail:
      'Accounting-style parentheses mark a negative amount, so (500) reads as -500 and ($1,200) reads as -1200. The currency symbol can sit inside or outside the parentheses: $(1,234.00) and ($1,234.00) both read as -1234.',
  },
  {
    id: 'percent',
    title: 'A percent sign is removed, not rescaled',
    detail:
      'A trailing percent sign is removed and the number is read as written, so 5% reads as 5 and 8.36% reads as 8.36. The value is never divided by 100 at this stage. Whether a percent column means a fraction or percentage points is decided by the field type you choose, and that scaling is applied later when the value is used, so a column that mixes 5% and 8.36 stays consistent.',
  },
  {
    id: 'not-a-number',
    title: 'What is kept as text',
    detail:
      'Values that are not plain numbers are kept as text and are not available for numeric ranges. This includes abbreviated magnitudes (125k, 1.2M), scientific notation (1e3), values with letters or units other than a recognized currency (10 bps, 3 months), and anything with extra punctuation (1.2.3).',
  },
  {
    id: 'precision',
    title: 'Precision',
    detail:
      'Numbers are stored with up to six decimal places. Values needing more precision than that are rounded.',
  },
  {
    id: 'magnitude',
    title: 'Very large numbers',
    detail:
      'Reading a number never changes it, whatever its size. Stored measurements hold up to 14 digits before the decimal point (well past one hundred trillion); a numeric field value beyond that is kept in your raw data but recorded as unmeasurable rather than stored inaccurately, and it is counted so you can see it happened. This is a storage limit, not a parsing rule. Long identifiers such as account or reference numbers are unaffected: they are kept as text.',
  },
];

/**
 * Canonical worked examples. The test pins each `parsed` to `parseNumericValue(input)`;
 * the page renders each row. Includes every ambiguous case the design review called out.
 */
export const PARSING_EXAMPLES: readonly ParsingExample[] = [
  { input: '1234', parsed: 1234, note: 'A plain whole number.' },
  { input: '1,234.56', parsed: 1234.56, note: 'Thousands commas are removed.' },
  { input: '1,234,567', parsed: 1234567, note: 'Correctly grouped thousands commas are removed.' },
  { input: '1,5', parsed: null, note: 'Commas that do not group digits in threes are kept as text, never read as 15.' },
  { input: '12,34', parsed: null, note: 'Malformed grouping is kept as text, never read as 1234.' },
  { input: '1,2345', parsed: null, note: 'A four-digit group is not a thousands group; kept as text.' },
  { input: '0,125', parsed: null, note: 'A zero before the comma is not US thousands grouping (it usually means a European decimal); kept as text, never read as 125.' },
  { input: '1.234,56', parsed: null, note: 'European-style decimals are kept as text, never read as 1.23456.' },
  { input: '$1,234.56', parsed: 1234.56, note: 'A leading currency symbol is removed.' },
  { input: '1234.56 USD', parsed: 1234.56, note: 'A trailing currency code is removed.' },
  { input: 'USD 100', parsed: 100, note: 'A leading currency code is removed.' },
  { input: '-3.5', parsed: -3.5, note: 'A leading minus sign is kept.' },
  { input: '+7', parsed: 7, note: 'A leading plus sign is allowed.' },
  { input: '.5', parsed: 0.5, note: 'A leading decimal point is allowed.' },
  { input: '(500)', parsed: -500, note: 'Parentheses mean negative.' },
  { input: '($1,200)', parsed: -1200, note: 'Parentheses plus a currency symbol.' },
  { input: '$(1,234.00)', parsed: -1234, note: 'The currency symbol outside the parentheses.' },
  { input: '$ (1,234.00)', parsed: -1234, note: 'A space between the symbol and the parentheses is fine.' },
  { input: '5%', parsed: 5, note: 'A percent sign is removed, the number is kept as written.' },
  { input: '(5%)', parsed: -5, note: 'A negative percent, sign from the parentheses.' },
  { input: '$-5', parsed: -5, note: 'The currency symbol is removed, the sign kept.' },
  { input: '-$1,234.56', parsed: -1234.56, note: 'The sign before the currency symbol also works.' },
  { input: '', parsed: null, note: 'Blank is empty, not a number.' },
  { input: 'NA', parsed: null, note: 'A missing-data placeholder is empty.' },
  { input: '-', parsed: null, note: 'A single dash is treated as missing.' },
  { input: '$ -', parsed: null, note: 'An accounting-style zero (a dash after the symbol) is treated as missing, never 0.' },
  { input: 'Pending', parsed: null, note: 'Text is kept as text.' },
  { input: '125k', parsed: null, note: 'Abbreviated magnitudes are kept as text.' },
  { input: '$1.2M', parsed: null, note: 'Abbreviated magnitudes are kept as text.' },
  { input: '10 bps', parsed: null, note: 'Unrecognized units are kept as text.' },
  { input: '5.', parsed: null, note: 'A trailing decimal point with no digits is not a number.' },
  { input: 'USD', parsed: null, note: 'A currency code with no number stays text.' },
  { input: '1e3', parsed: null, note: 'Scientific notation is not supported.' },
  { input: '1.2.3', parsed: null, note: 'Extra punctuation is not a number.' },
];

// ── Dates ─────────────────────────────────────────────────────────────────────

export const DATE_PARSING_RULES: readonly ParsingRule[] = [
  {
    id: 'date-one-grammar',
    title: 'One consistent rule for every date column',
    detail:
      'A date value is read the same way no matter which field it maps to. Formats that can only mean one thing are always accepted; a date written as two numbers, where the day and month could be swapped, is read using the order declared for that column (suggested from your data, US Month/Day when the data cannot settle it). A value that does not fit is kept as text and treated as empty for date fields, never guessed.',
  },
  {
    id: 'date-unambiguous',
    title: 'Formats that are always recognized',
    detail:
      'Year-first dates (2024-01-15, 2024/1/15, 20240115), month codes (2024-01, 202401), and dates with a written month name (Jan-2024, January 2024, 15-Jan-2024, Jan 15, 2024) are always read, whatever the column\'s declared order. Month names can be the 3-letter abbreviation or the full name, in any capitalization. A month-level value (Jan-2024 or 2024-01) is read as the month, with the day pinned to the first.',
  },
  {
    id: 'date-declared-order',
    title: 'Day/Month order is declared per column',
    detail:
      'A date written as two numbers and a year (04/07/2025 or 01-15-2024) could be Month/Day or Day/Month. Each column carries a declared order. Your own data settles it when it can: a value whose first number is over 12 proves Day/Month, and a value whose second number is over 12 proves Month/Day. When nothing in the column settles it, the column is read in the US Month/Day order; that assumption is shown with the column so you can correct it before your data is used. A value that does not fit the column\'s order is kept as text, never silently flipped.',
  },
  {
    id: 'date-real-calendar',
    title: 'Only real calendar dates',
    detail:
      'A value must be a date that actually exists. February 30th, April 31st, and February 29th in a non-leap year are kept as text, not rounded to a nearby date. February 29th in a leap year (02/29/2024) is a valid date.',
  },
  {
    id: 'date-not-a-date',
    title: 'What is kept as text',
    detail:
      'Values that do not fit any recognized date format, or that do not fit the column\'s declared order, are kept as text and treated as empty for date fields. A bare 4-digit year (2024) is not read as a date, because it cannot be told apart from an ordinary number.',
  },
];

/**
 * One worked date example. `ordinal` is BOTH the test oracle (it must equal
 * `parseDateWithFormat(input, format).ordinal`) and what the page shows, so the
 * page is guaranteed to match the parser. `null` means the value is not
 * recognized as a date under that declared format and is kept as text.
 */
export interface DateParsingExample {
  readonly input: string;
  /** The column's declared date format the value is read under. */
  readonly format: DeclaredDateFormat;
  readonly ordinal: number | null;
  /** The recognized granularity, or null when the value is kept as text. */
  readonly grain: 'day' | 'month' | null;
  readonly note: string;
}

/**
 * Canonical worked date examples. `date-parse.test.ts` pins each row to
 * `parseDateWithFormat(input, format)`; the page renders each row.
 */
export const DATE_PARSING_EXAMPLES: readonly DateParsingExample[] = [
  { input: '2024-01-15', format: 'iso', ordinal: 20240115, grain: 'day', note: 'A year-first date is always recognized.' },
  { input: '20240115', format: 'iso', ordinal: 20240115, grain: 'day', note: 'An 8-digit date code.' },
  { input: '2024-01', format: 'iso', ordinal: 20240101, grain: 'month', note: 'A month code is read as the month, day pinned to the first.' },
  { input: '04/07/2025', format: 'mdy', ordinal: 20250407, grain: 'day', note: 'Under Month/Day order this is April 7th, 2025.' },
  { input: '04/07/2025', format: 'dmy', ordinal: 20250704, grain: 'day', note: 'The same value under Day/Month order is July 4th, 2025.' },
  { input: '01-15-2024', format: 'mdy', ordinal: 20240115, grain: 'day', note: 'Dashes work the same as slashes for a declared order.' },
  { input: '31/12/2024', format: 'mdy', ordinal: null, grain: null, note: 'A value that does not fit the declared order is kept as text, not silently flipped.' },
  { input: 'Jan-2024', format: 'month_name', ordinal: 20240101, grain: 'month', note: 'A written month name is always unambiguous.' },
  { input: 'January 2024', format: 'month_name', ordinal: 20240101, grain: 'month', note: 'Full month names work too, in any capitalization.' },
  { input: '15-Jan-2024', format: 'month_name', ordinal: 20240115, grain: 'day', note: 'A day with a written month name.' },
  { input: 'Jan 15, 2024', format: 'month_name', ordinal: 20240115, grain: 'day', note: 'The written-out style is recognized.' },
  { input: '15-Jan-2024', format: 'mdy', ordinal: 20240115, grain: 'day', note: 'Month-name dates are read under any declared order.' },
  { input: '02/29/2024', format: 'mdy', ordinal: 20240229, grain: 'day', note: '2024 is a leap year, so February 29th is a real date.' },
  { input: '02/29/2023', format: 'mdy', ordinal: null, grain: null, note: '2023 is not a leap year; kept as text.' },
  { input: '02/30/2024', format: 'mdy', ordinal: null, grain: null, note: 'February 30th does not exist; kept as text.' },
  { input: '04/31/2023', format: 'mdy', ordinal: null, grain: null, note: 'April has 30 days; kept as text.' },
  { input: '04/07/2025', format: 'iso', ordinal: null, grain: null, note: 'A two-number date does not fit a column declared year-first; kept as text.' },
  { input: '2024', format: 'mdy', ordinal: null, grain: null, note: 'A bare year is never read as a date.' },
];
